// tslint:disable:no-document-query-selectors

export interface EventListenerItem {
  fn: Function;
  event: string;
  _boundFn?: Function;
}

interface RegisterItem {
  selector: string;
  cls: any;
  instances?: any[];
}

/**
 * The local register which is used to keep track
 * of all registered DOMComponent and DOMDirective
 * classes.
 */
export const register: RegisterItem[] = [];

/**
 * Instance of MutationObserver watching for DOM node changes.
 */
export let observer: MutationObserver;

/**
 * Local property on which a reference of the DOM HostElement is stored.
 */
export const HOST_ELEMENT_KEY = '__hostElmBinding';

/**
 * Local property on which a unique ID for the Class is stored.
 */
export const CLASS_UNIQUE_IDENTIFIER = '__id';

/**
 * Property on which registered directives are stored.
 */
export const INST_DIRECTIVE_REGISTER = '__aaDirectives';

/**
 * Holds an array of event listeners which should be added and removed
 * during connection and disconnection.
 */
export const EVENT_LISTENER_BINDINGS = '__eventListeners';

/**
 * Method name for 'before' connection chain. This callback
 * is ran before any rendering takes place and should be used to setup
 * properties and other 'required' stuff.
 */
export const BEFORE_CONNECTED_CALLBACK = 'beforeConnected';

/**
 * Method name for connection chain. This callback runs right
 * after everything is rendered.
 */
export const CONNECTED_CALLBACK = 'connected';

/**
 * Method name for disconnection chain
 */
export const DISCONNECTED_CALLBACK = 'disconnected';

/**
 * Implement the {@link OnConnect} interface and its methods in your Component or Directive class
 * to specify code which should be executed when the Component or Directive has been
 * 'connected' successfully.
 *
 * This can, for example, be usefull when setting up additional event listeners.
 */
export interface OnConnect {
  connected(): void;
}

/**
 * Implemesnting the {@link OnDisconnect} interface and its belonging methods allows to
 * define code which should be executed just before the element is removed from the DOM.
 */
export interface OnDisconnect {
  disconnected(): void;
}

/**
 * AaElement interface which includes our (optional) INT_DIRECTIVE_REGISTER property
 * which is used to hold a registry of active directives.
 */
export interface AaElement extends Element {
  [INST_DIRECTIVE_REGISTER]?: any;
}

/**
 * Creates a new instance of the given Directive class and 'connects' it to the
 * provided element.
 *
 * This takes care of the actual setup procedure. The provided element will be checked
 * to see if there's already an instance of the given directive class connected. If
 * this is not the case a new instance of the Directive class will be created and
 * applied to the element.
 *
 * A new inheritance for the element is created, allowing the directive class to
 * access the DOM element in its constructor.
 *
 * After the instance is created the 'connected' callback is executed, which allows
 * further initialization to take place.
 *
 * @param element element to connect directive class to
 * @param Cls directive class to create new instance from
 * @param args provided optional arguments
 */
export const setupDirective = (element: AaElement, Cls: any, ...args: any[]) => {
  if (element[INST_DIRECTIVE_REGISTER] && element[INST_DIRECTIVE_REGISTER][Cls[CLASS_UNIQUE_IDENTIFIER]]) {
    // Return when directive has already been registered
    return;
  }

  // Inherit given Directive class so we can add the DOM element before any
  // constructing happens. This allows us to have access to the DOM element
  // in our class' 'constructor'.
  function DirectiveCls(this: any) {
    this[HOST_ELEMENT_KEY] = function () { return element; };
    Cls.apply(this, args);
  }
  DirectiveCls.prototype = Object.create(Cls.prototype);

  // Create new instance
  const inst = new (DirectiveCls as any)();

  // Register as directive on element
  element[INST_DIRECTIVE_REGISTER] = element[INST_DIRECTIVE_REGISTER] || {};
  element[INST_DIRECTIVE_REGISTER][Cls[CLASS_UNIQUE_IDENTIFIER]] = inst;

  // Add instance to register and return
  const registerRecord = register.find((reg) => reg.cls === Cls);
  if (registerRecord !== undefined) {
    registerRecord.instances = registerRecord.instances || [];
    registerRecord.instances.push(inst);
  }

  // Runs before connection callback.
  if (inst[BEFORE_CONNECTED_CALLBACK]) {
    inst[BEFORE_CONNECTED_CALLBACK]();
  }

  // Fire connectedCallback which takes care of element registration
  if (inst[CONNECTED_CALLBACK]) {
    inst[CONNECTED_CALLBACK]();
  }

  return inst;
};

/**
 * Helper method to render a new HTMLelement element based on the given
 * DomComponent class.
 *
 * This can be used to render new Components in the DOM using a reference
 * to the actual Component's class. A matching selector for the given reference
 * is looked-up in the 'register' and used when creating the new HTMLElement.
 *
 * Optionally additional arguments can be provided. These arguments will be applied
 * to the Component's constructor and can be usefull when passing along data.
 *
 * The returned element already has its Directive instance connected to it.
 *
 * In the example below the 'Alphabet' component will create new instances of
 * the 'Character' component and apply them to the 'chars' wrapper div.
 *
 * ```typescript
 * @example
 * // Your component class
 * @DomComponent({
 *  selector: 'aa-character',
 *  template: `I'm {{char}}`
 * })
 * class Character {
 *  constructor(char: string) {}
 * }
 *
 * // Somewhere else
 * @DomComponent({
 *  selector: 'aa-alphabet',
 *  template: `<div class="chars"><div>`
 * })
 * class Alphabet implements OnConnect {
 *  @Child('.chars')
 *  private readonly charWrapper: HTMLElement;
 *
 *  connected() {
 *    const chars = ['a', 'b', 'c'];
 *    chars.forEach((char) => {
 *      this.charWrapper.appendChild( createComponent(Alphabet, char) );
 *    });
 *  }
 * }
 * ```
 *
 * @param cls component class
 * @param args provided optional arguments
 */
export const createComponent = (cls: any, ...args: any[]): HTMLElement => {
  const registerType = register.find((reg) => reg.cls === cls);

  if (!registerType) {
    throw new Error(`No DomComponent registered for ${cls.name}. Make sure to decorate your Component Classes with "@DomComponent".`);
  }

  const element: HTMLUnknownElement = <HTMLUnknownElement>document.createElement(registerType.selector);

  setupDirective(element, cls, ...args);

  return element;
};

/**
 * Registers new Directives and sets up the DOM observer, if not already done.
 * The observer watches for new and removed nodes in the DOM's <body>.
 *
 * @param selector selector name for Directive
 * @param cls the Directive Class
 */
export const registerNewDirective = (selector: string, cls: any) => {
  // Check if selector doesn't already exist
  const found = register.filter((s) => s.selector === selector);
  if (found.length > 0) {
    throw new Error('Directive with selector already registered');
  }

  // Sets the selector as 'unique identifier' on the class
  cls[CLASS_UNIQUE_IDENTIFIER] = selector;

  // Add selector and cls to registration
  register.push({
    selector,
    cls,
  });

  // Setup observer to whatch for new and removed elements in the DOM's body
  if (!observer) {
    // Check if a MutationObserver implementation is found.
    if ('MutationObserver' in window === false) {
      console.error('No MutationObserver implementation found. Please include the MutationObserver polyfill.');
      return;
    }
    observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length > 0) {
          addedDomCheck(mutation.addedNodes);
        }
        if (mutation.removedNodes.length > 0) {
          removedDomCheck(mutation.removedNodes);
        }
      });
    });
    observer.observe(document.body || document.documentElement, { subtree: true, childList: true });

    // Execute domcheck at initial DOMContentLoaded event
    document.addEventListener('DOMContentLoaded', () => addedDomCheck());
  }
};

/**
 * Checks the given addedNodes for new elements which have matching selectors in the 'register'.
 *
 * New directive instance will be created for any matching DOM element. The returned instance is saved
 * in the register for later reference.
 *
 * @param addedNodes NodeList
 */
export const addedDomCheck = (addedNodes: NodeList | null = null) => {
  let performLookup = (addedNodes === null);

  if (addedNodes) {
    for (let x = 0; x < addedNodes.length; x++) {
      // We're only interested in new Nodes of type ELEMENT_NODE.
      if (addedNodes[x].nodeType === Node.ELEMENT_NODE) {
        performLookup = true;
      }
    }
  }

  if (performLookup || addedNodes === null) {
    // Go through all lookup elements and setup directives
    register.forEach((s) => {
      const matches = document.querySelectorAll(s.selector);
      s.instances = s.instances || [];
      for (let x = 0; x < matches.length; x++) {
        setupDirective(matches[x], s.cls);
      }
    });
  }
};

/**
 * Gracefully disconnects directive instances on the removed nodes and their children.
 *
 * The local registry is used to check whether the node contains any of the active registered
 * 'instances'. If so it gracefully disconnects the directive by calling the disconnected callback
 * and removing it from the registry.
 *
 * @param removedNodes
 */
export const removedDomCheck = (removedNodes: NodeList) => {
  for (let x = 0; x < removedNodes.length; x++) {
    if (removedNodes[x].nodeType === Node.ELEMENT_NODE) {
      register.forEach((reg) => {
        if (reg.instances) {
          // Go through instances, search for nodes which should be disconnected and clean them up
          reg.instances = reg.instances.filter((inst) => {
            const instElm: Node = inst[HOST_ELEMENT_KEY]();
            const removedNode = removedNodes[x];
            const containsInstance = (typeof removedNode['contains'] === 'function') ? removedNode.contains(instElm) : false;
            if (containsInstance && inst[DISCONNECTED_CALLBACK]) {
              inst[DISCONNECTED_CALLBACK]();
            }
            return !containsInstance;
          });
        }
      });
    }
  }
};

/**
 * Verifies the given selector to make sure it starts with aa-.
 *
 * @param selector
 * @throws {Error} when selector doesn't start with aa-
 */
export function verifyAaSelector(selector: string) {
  if (selector.indexOf('aa-') !== 0) {
    throw new Error(`Your selector should start with 'aa-'. (eg: aa-${selector})`);
  }
}

/**
 * Prepares class prototypes to add support for our @HostEvent decorator.
 * Takes and returns an Object.prototype.
 *
 * This supports both our Component and Directive directives.
 */
export function setupPrototype(cls: any) {
  // Extend connectedCallback (if any)
  const connectedCallback = cls.prototype[BEFORE_CONNECTED_CALLBACK] || function () { };
  cls.prototype[BEFORE_CONNECTED_CALLBACK] = function () {
    // Array of EventListener[]
    cls.prototype[EVENT_LISTENER_BINDINGS] = cls.prototype[EVENT_LISTENER_BINDINGS] || [];

    // Bind all event listeners
    cls.prototype[EVENT_LISTENER_BINDINGS].forEach((event: EventListenerItem) => {
      const boundEvent = event._boundFn = event.fn.bind(this);
      const hostInst = this[HOST_ELEMENT_KEY]();
      hostInst.addEventListener(event.event, boundEvent);
    });

    // Call callback
    connectedCallback.call(this);
  };

  // Extend disconnection callback
  const disconnectedCallback = cls.prototype[DISCONNECTED_CALLBACK] || function () { };
  cls.prototype[DISCONNECTED_CALLBACK] = function () {
    // Remove all event listeners
    cls.prototype[EVENT_LISTENER_BINDINGS].forEach((event: EventListenerItem) => {
      const hostInst = this[HOST_ELEMENT_KEY]();
      hostInst.removeEventListener(event.event, event._boundFn);
    });

    // Call disconnection callback
    disconnectedCallback.call(this);
  };

  return cls;
}
